import { configureStore, createListenerMiddleware, isRejectedWithValue } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import { baseApi } from 'store/baseApi';
import { usersApi } from 'store/entities/users/api';
import { logout, usersSlice } from 'store/entities/users/slice';
import { DIALOG_PROPS_PATH, openDialogAction } from 'store/entities/ui/slice';
import Sentry from 'sentry';

import reducer from './reducers';

const SENTRY_IGNORED_REDUCERS = [baseApi.reducerPath, usersSlice.name];

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: (state) =>
    Object.keys(state).reduce(
      (acc, key) => (SENTRY_IGNORED_REDUCERS.includes(key) ? acc : { ...acc, [key]: state[key] }),
      {},
    ),
  actionTransformer: (action) =>
    SENTRY_IGNORED_REDUCERS.some((reducerName) => action.type.includes(reducerName)) ? null : action,
});

const rtkQueryErrorMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
    if (isRejectedWithValue(action) && !!action?.payload?.status && action.payload.status >= 500) {
      dispatch(logout());
    }

    return next(action);
  };

const logoutMiddleware = createListenerMiddleware();

logoutMiddleware.startListening({
  actionCreator: logout,
  effect: (_action, { dispatch }) => {
    dispatch(baseApi.util.resetApiState());
  },
});

const getCurrentUserMiddleware = createListenerMiddleware();

getCurrentUserMiddleware.startListening({
  matcher: usersApi.endpoints.getCurrentUser.matchFulfilled,
  effect: (action, { dispatch, getState }) => {
    const state = getState();

    if (state.usersSlice.currentUser !== action.payload) {
      dispatch({ type: 'usersSlice/setCurrentUser', payload: action.payload });
    }
  },
});

const configureRootStore = () =>
  configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoreActions: [openDialogAction],
          ignoredPaths: [DIALOG_PROPS_PATH],
        },
      }).concat(
        rtkQueryErrorMiddleware,
        logoutMiddleware.middleware,
        getCurrentUserMiddleware.middleware,
        baseApi.middleware,
      ),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
  });

const store = configureRootStore();

// enable listener behavior for the store
setupListeners(store.dispatch);

export default store;
